"use client";

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./auth";
import { supabase } from "./supabase";
import { selectContext, selectContextWithMetadata, estimateTokens } from "./vault-tokens";
import { isFileSystemAccessSupported, pickVaultDirectory, pickDirectory, classifyFolder, commitVaultDirectory, restoreDirectoryHandle, disconnectVault as disconnectLocal, readLocalVault, writeLocalNote, deleteLocalNote, validateVaultStructure } from "./vault-local";
import { emitSignal } from "./signal";
import { readFulkitNotes, updateContextMode, listNotes, searchNotes } from "./vault-fulkit";
import { syncProfileToVault } from "./vault-writeback";
import { clearLegacyHandle, getMachine, setMachine, clearMachine } from "./vault-idb";
import { scanAndImportToSupabase } from "./vault-disk-import";

// Per-user localStorage key for the last successful disk-import timestamp.
// Lets /settings/vault render "Last scanned: X ago" across sessions.
const LAST_SCAN_KEY = (uid) => `fulkit-vault-last-scan-${uid}`;

// One-shot mirror of identity preferences to _FULKIT/profile.md. Fires on
// vault connect and restore — chat-turn syncs handle ongoing updates.
async function mirrorProfile(handle) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    const res = await fetch("/api/preferences/me", { headers: { Authorization: `Bearer ${session.access_token}` } });
    if (!res.ok) return;
    const body = await res.json();
    if (body?.preferences) await syncProfileToVault(handle, body.preferences);
  } catch {
    // best-effort — chat-turn sync will catch up
  }
}

const VaultContext = createContext(null);

export function VaultProvider({ children }) {
  const { user, profile } = useAuth();
  const vaultBudget = profile?.role === "owner" ? 100000 : 50000;

  // V2 Step 14d — storage_mode collapse to mirrorFolderConnected derived
  // state. Single source-of-truth: mirror_folder_connected boolean from
  // preferences. Replaces V1 storage_mode toggle entirely. SQL column
  // drop deferred to dedicated future ship (30+ days telemetry).
  const [mirrorFolderConnected, setMirrorFolderConnectedState] = useState(false);
  const [loading, setLoading] = useState(true);

  // Local Vault state (gated by mirrorFolderConnected)
  const [directoryHandle, setDirectoryHandle] = useState(null);

  // V2 Step 11a — permission decay state.
  // Set during init when IDB has a handle but queryPermission returns
  // "prompt" (decayed — Chrome silently revokes after ~2 weeks of
  // inactivity) or "denied" (user explicitly blocked). Drives the
  // V2PermissionBanner banner which surfaces a [Reconnect] button.
  // pendingDirectoryHandle holds the IDB-retrieved handle awaiting a
  // user-gesture-triggered requestPermission(); needed because Chrome
  // rejects requestPermission() outside user gestures.
  const [permissionState, setPermissionState] = useState("granted");
  const [pendingDirectoryHandle, setPendingDirectoryHandle] = useState(null);

  // Disk-import scan state — surfaces in /settings/vault as a "Scan
  // vault" button + live progress. Auto-fires once on fresh connect /
  // commit so pre-existing files in the picked folder become searchable
  // immediately. Does NOT auto-fire on session-restore (would re-scan
  // every visit; too aggressive). lastScanAt is per-user, localStorage-
  // persisted so the UI survives reloads.
  const [scanInProgress, setScanInProgress] = useState(false);
  const [scanProgress, setScanProgress] = useState(null); // { scanned, upserted } | null
  const [lastScanAt, setLastScanAt] = useState(null);
  const [lastScanResult, setLastScanResult] = useState(null);

  // V2 Step 14d — Load mirror_folder_connected preference on mount.
  // Depend on user?.id (primitive) — setUser({...}) creates a new object reference on
  // every token refresh, and re-running init can race with the live handle.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    async function init() {
      // One-time cleanup: remove the pre-fix unkeyed vault handle
      clearLegacyHandle();

      // V2 Step 14d — Read mirror_folder_connected from preferences (3s
      // timeout — default to safe value if slow). Single source-of-truth
      // since storage_mode collapse. Missing → false (cloud-only default).
      // RLS filters by auth.uid() at the DB level (existing pattern, no
      // user_id arg here). vault_state_divergence detection removed —
      // single key cannot diverge from itself.
      const { data, error } = await supabase
        .from("preferences")
        .select("key, value")
        .eq("key", "mirror_folder_connected")
        .abortSignal(AbortSignal.timeout(3000));

      const prefs = error || !data ? {} : Object.fromEntries(data.map((r) => [r.key, r.value]));
      const mirrorConnected = prefs.mirror_folder_connected === "true";
      setMirrorFolderConnectedState(mirrorConnected);

      // Local Vault: try to restore directory handle (2s timeout + permission verify)
      if (mirrorConnected && isFileSystemAccessSupported()) {
        try {
          const handle = await Promise.race([
            restoreDirectoryHandle(userId),
            new Promise((_, r) => setTimeout(() => r(new Error("timeout")), 2000)),
          ]);
          if (handle) {
            // Verify permission is still granted — stale handles get cleared here
            const perm = await Promise.race([
              handle.queryPermission({ mode: "readwrite" }),
              new Promise(resolve => setTimeout(() => resolve("denied"), 1000)),
            ]);
            if (perm === "granted") {
              setDirectoryHandle(handle);
              mirrorProfile(handle);
            } else {
              // V2 Step 11a — permission decayed (Chrome silent revoke after
              // ~2 weeks inactivity) or explicitly blocked. Retain handle as
              // "pending" so V2PermissionBanner's [Reconnect] button can fire
              // requestPermission() on user gesture (Chrome rejects without one).
              // Don't set directoryHandle — chat code paths gate on it.
              setPendingDirectoryHandle(handle);
              if (perm === "prompt") {
                setPermissionState("decayed");
                emitSignal("vault_permission_decayed", "info", {});
              } else {
                setPermissionState("blocked");
                emitSignal("vault_permission_explicitly_blocked", "warning", {});
              }
            }
          }
        } catch {
          // Handle expired, permission denied, or timeout — user reconnects from Settings
        }
      }

      setLoading(false);
    }

    init();
  }, [userId]);

  // V2 Step 14d — Derived state from mirrorFolderConnected (single source).
  const isSupported = mirrorFolderConnected ? isFileSystemAccessSupported() : true;
  const vaultConnected = mirrorFolderConnected && directoryHandle !== null;
  const isReady = !mirrorFolderConnected || vaultConnected;

  // V2 Step 14a — 30-sec permFailCount poll removed. 11a chat-open
  // queryPermission detection is the V2 detection mechanism (immediate,
  // user-actionable via V2PermissionBanner). The V1 30s poll was
  // defense-in-depth secondary check; after 11a coverage, it became
  // redundant. validateVaultStructure still runs per chat-open via
  // ensureVaultBootstrap → 11b reconcile chain. fulkit-vault-change
  // CustomEvent had zero listeners (verified by grep) — pure dead.

  // Shared note-fetching logic — 8s hard cap prevents chat from hanging
  const getRawNotes = useCallback(async () => {
    try {
      const notes = await Promise.race([
        (async () => {
          if (mirrorFolderConnected && directoryHandle) {
            return await readLocalVault(directoryHandle);
          }
          return await readFulkitNotes(supabase, user?.id);
        })(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Vault read timeout (8s)")), 8000)),
      ]);
      return notes;
    } catch (err) {
      emitSignal("vault_read_timeout", "warning", { mirror_folder_connected: mirrorFolderConnected, error: err?.message });
      return [];
    }
  }, [mirrorFolderConnected, directoryHandle]);

  // Read context from active source, apply token budget with relevance scoring
  const getContext = useCallback(async (message) => {
    try {
      const notes = await getRawNotes();
      return selectContext(notes, message, vaultBudget);
    } catch (err) {
      console.error("[vault] getContext error:", err.message);
      return [];
    }
  }, [getRawNotes, vaultBudget]);

  // Same as getContext but returns metadata for the UI indicator
  const getContextWithMeta = useCallback(async (message) => {
    try {
      const notes = await getRawNotes();
      return selectContextWithMetadata(notes, message, vaultBudget);
    } catch (err) {
      console.error("[vault] getContextWithMeta error:", err.message);
      return { selected: [], metadata: { includedCount: 0, alwaysCount: 0, totalTokens: 0 } };
    }
  }, [getRawNotes, vaultBudget]);

  // Get note list for settings browser (metadata only, no content)
  const getNoteList = useCallback(async () => {
    if (mirrorFolderConnected && directoryHandle) {
      const files = await readLocalVault(directoryHandle);
      return files.map((f, i) => ({
        id: `local-${i}`,
        title: f.title,
        context_mode: f.path && (f.path.includes("_FULKIT/") || f.path.includes("_CHAPPIE/")) ? "always" : "available",
        source: "local",
        folder: f.path,
        tokenEstimate: estimateTokens(f.content),
      }));
    }
    return await listNotes(supabase, user?.id);
  }, [mirrorFolderConnected, directoryHandle, user]);

  // Toggle context_mode for a note (cloud-only — Local Vault has no DB rows)
  const updateNoteMode = useCallback(async (noteId, mode) => {
    if (mirrorFolderConnected) return;
    await updateContextMode(noteId, mode, supabase, user?.id);
  }, [mirrorFolderConnected, user]);

  // Search all notes (including off) by title/folder — for /recall
  const recallNotes = useCallback(async (query) => {
    if (mirrorFolderConnected) return [];
    return await searchNotes(query, supabase, user?.id);
  }, [mirrorFolderConnected, user]);

  // Hydrate last-scanned timestamp from localStorage on mount + when user changes.
  useEffect(() => {
    if (!userId || typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(LAST_SCAN_KEY(userId));
      setLastScanAt(raw ? Number(raw) : null);
    } catch {}
  }, [userId]);

  // Run a disk-import scan of the connected folder. Streams progress
  // via setScanProgress so /settings/vault can render a live count.
  // Idempotent — UPSERT on (user_id, filed_path) means re-runs refresh
  // previews without duplicating rows. Returns the scan result.
  const scanVault = useCallback(async (handle = null) => {
    const fsHandle = handle || directoryHandle;
    if (!fsHandle || !userId || scanInProgress) return null;
    setScanInProgress(true);
    setScanProgress({ scanned: 0, upserted: 0 });
    try {
      const result = await scanAndImportToSupabase(fsHandle, supabase, userId, {
        onProgress: (p) => setScanProgress(p),
      });
      const ts = Date.now();
      setLastScanAt(ts);
      setLastScanResult(result);
      try { window.localStorage.setItem(LAST_SCAN_KEY(userId), String(ts)); } catch {}
      return result;
    } finally {
      setScanInProgress(false);
    }
  }, [directoryHandle, userId, scanInProgress]);

  // V2 Step 13b — derive friendly name for machine record from
  // navigator hints. Auto-generated, NOT user-editable in 13b. UA
  // high-entropy fields aren't universally available — fall back to
  // legacy navigator.platform when userAgentData is absent.
  function deriveFriendlyName() {
    if (typeof navigator === "undefined") return "Device · Browser";
    const ua = navigator.userAgentData;
    let platform = ua?.platform || navigator.platform || "Device";
    let brand = "Browser";
    if (Array.isArray(ua?.brands)) {
      const match = ua.brands.find((b) => /Chrome|Edge|Brave|Arc|Opera/.test(b.brand));
      if (match) brand = match.brand;
    } else if (/Edg\//.test(navigator.userAgent || "")) {
      brand = "Microsoft Edge";
    } else if (/Brave/.test(navigator.userAgent || "")) {
      brand = "Brave";
    } else if (/Chrome/.test(navigator.userAgent || "")) {
      brand = "Chrome";
    }
    return `${platform} · ${brand}`;
  }

  // V2 Step 13b — register or update machine record on vault connect.
  // Generates random UUID on first connect (machineId persists across
  // disconnect/reconnect cycles — identity is the device, not the
  // connection state). Re-pick path: same machineId, swap folderName.
  // Per V2 spec line 712 "per-machine state in IndexedDB".
  async function registerOrUpdateMachine(handle, isRePick) {
    if (!userId || !handle) return;
    try {
      const existing = await getMachine(userId);
      const now = Date.now();
      const machineId = existing?.machineId
        || (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `m-${now}-${Math.random().toString(36).slice(2, 10)}`);
      const record = {
        machineId,
        friendlyName: existing?.friendlyName || deriveFriendlyName(),
        folderName: handle.name || "fulkit-vault",
        connectedAt: existing?.connectedAt || now,
        lastSeenAt: now,
      };
      await setMachine(userId, record);
      if (isRePick) {
        emitSignal("vault_machine_repicked", "info", {
          machineId, folderName: record.folderName,
        });
      } else {
        emitSignal("vault_machine_added", "info", {
          machineId, backfill: false,
        });
      }
    } catch (err) {
      console.error("[vault:machine] registerOrUpdateMachine failed", err?.message);
    }
  }

  // Local Vault: connect vault directory (legacy — picks + auto-validates + commits)
  const connectVault = useCallback(async () => {
    if (!isFileSystemAccessSupported()) return;
    if (!userId) return;
    const handle = await pickVaultDirectory(userId);
    setDirectoryHandle(handle);
    mirrorProfile(handle);
    // V2 Step 13b — register machine identity. isRePick detection: if a
    // machine record already exists for this user, this is a re-pick of
    // the folder (machineId stays, folderName updates). Otherwise it's
    // a fresh add (new machineId).
    const isRePick = !!(await getMachine(userId).catch(() => null));
    registerOrUpdateMachine(handle, isRePick);
    // Fire-and-forget initial scan so pre-existing folder content
    // becomes searchable immediately. Caller doesn't await — UI shows
    // progress via scanInProgress + scanProgress.
    scanVault(handle);
  }, [userId, scanVault]);

  // Local Vault: commit a previously-picked handle (used by v5.1 Screen 06
  // flow which classifies + prompts before committing).
  const setConnectedDirectory = useCallback(async (handle) => {
    if (!userId || !handle) return;
    await commitVaultDirectory(userId, handle);
    setDirectoryHandle(handle);
    mirrorProfile(handle);
    // V2 Step 13b — register machine identity. Same isRePick detection
    // as connectVault path — if a machine record already exists for
    // this user, swap folderName; otherwise generate fresh machineId.
    const isRePick = !!(await getMachine(userId).catch(() => null));
    registerOrUpdateMachine(handle, isRePick);
    scanVault(handle);
    // V2 Step 9 — bulletproof brain/trunk + user-editable templates.
    // Connect-time bootstrap doesn't have cloud content blobs (those
    // come from /api/chat/greeting on chat-open). Folder-shell drift
    // detection + restore + user-editable template fills run here so
    // a fresh-connected vault has README/VAULT-GUIDE/thank-you ready
    // immediately. Fülkit-managed files (ledger/pulse/profile) get
    // populated by the next chat-open's bootstrap. Fire-and-forget.
    import("./vault-bootstrap").then(({ ensureVaultBootstrap }) => {
      ensureVaultBootstrap(handle, {}).catch((err) => {
        console.error("[vault:connect] bootstrap failed", err?.name, err?.message);
      });
    }).catch((err) => console.error("[vault:connect] vault-bootstrap module load failed", err?.message));
  }, [userId, scanVault]);

  // Local Vault: disconnect vault
  const disconnectVault = useCallback(async () => {
    if (!userId) return;
    // V2 Step 13b — clear machine record on disconnect. machineId
    // identity ends with the device's connection state ending. If
    // user re-connects later, fresh machineId generated (browser
    // cache clear shape — same trade-off, same outcome).
    let priorMachineId = null;
    try {
      const existing = await getMachine(userId);
      priorMachineId = existing?.machineId || null;
      await clearMachine(userId);
    } catch (err) {
      console.error("[vault:machine] clearMachine failed", err?.message);
    }
    await disconnectLocal(userId);
    setDirectoryHandle(null);
    if (priorMachineId) {
      emitSignal("vault_machine_disconnected", "info", { machineId: priorMachineId });
    }
  }, [userId]);

  // Write a note to the local vault folder (Local Vault only)
  const saveToLocalVault = useCallback(async (folder, title, content) => {
    if (!mirrorFolderConnected || !directoryHandle) return null;
    try {
      const path = await writeLocalNote(directoryHandle, folder, title, content);
      return path;
    } catch (err) {
      console.error("[vault] Local write failed:", err.message);
      return null;
    }
  }, [mirrorFolderConnected, directoryHandle]);

  // Delete a note from the local vault folder (Local Vault only)
  const deleteFromLocalVault = useCallback(async (folder, title) => {
    if (!mirrorFolderConnected || !directoryHandle) return;
    try {
      await deleteLocalNote(directoryHandle, folder, title);
    } catch (err) {
      console.error("[vault] Local delete failed:", err.message);
    }
  }, [mirrorFolderConnected, directoryHandle]);

  // V2 Step 14d — setStorageMode callback removed. Single source-of-truth
  // is mirror_folder_connected; setMirrorFolderConnected handles the
  // write path. Action-completion side-effect (vault feature_tag) folded
  // into setMirrorFolderConnected to preserve onboarding action resolve.

  // V2 Step 11a — retry the FS Access permission grant for a pending handle.
  // Called from V2PermissionBanner [Reconnect] / [Try Again] button on
  // user gesture. Chrome rejects requestPermission() outside user gestures,
  // so this MUST fire from a click handler — banner enforces that contract.
  // On grant: handle moves from pendingDirectoryHandle → directoryHandle,
  // permissionState clears to "granted", banner condition fails, banner
  // unmounts. On non-grant: state stays at decayed/blocked, banner stays.
  const retryPermission = useCallback(async () => {
    if (!pendingDirectoryHandle) return { granted: false, error: "no_pending_handle" };
    try {
      const perm = await pendingDirectoryHandle.requestPermission({ mode: "readwrite" });
      if (perm === "granted") {
        setDirectoryHandle(pendingDirectoryHandle);
        setPendingDirectoryHandle(null);
        setPermissionState("granted");
        mirrorProfile(pendingDirectoryHandle);
        emitSignal("vault_permission_reconnect_succeeded", "info", {});
        return { granted: true };
      }
      // User declined or browser-blocked at the OS level.
      emitSignal("vault_permission_reconnect_failed", "warning", { permission: perm });
      // Re-check queryPermission to surface "denied" → blocked banner mode
      // even if user just declined the prompt.
      if (perm === "denied") setPermissionState("blocked");
      return { granted: false, permission: perm };
    } catch (err) {
      emitSignal("vault_permission_reconnect_failed", "warning", { error: err?.message || null });
      return { granted: false, error: err?.message || "unknown" };
    }
  }, [pendingDirectoryHandle]);

  // V2 Step 10a + 14d — set mirror_folder_connected DB flag + state.
  // Single source-of-truth post-14d collapse. DB-fail-without-state-revert
  // pattern: if the upsert fails, local state stays at its prior value
  // (no divergence possible — single key). Severity 'error' on emit
  // because user-perceived state has diverged from DB state — vault-trust
  // integrity event. Also resolves the onboarding "vault" feature_tag
  // action on connected=true (folded from removed setStorageMode).
  const setMirrorFolderConnected = useCallback(async (connected) => {
    if (!user) return;
    try {
      const { error } = await supabase.from("preferences").upsert({
        user_id: user.id,
        key: "mirror_folder_connected",
        value: connected ? "true" : "false",
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id,key" });
      if (error) {
        emitSignal("mirror_folder_connected_set_failed", "error", { connected, error: error.message });
        return;
      }
    } catch (err) {
      emitSignal("mirror_folder_connected_set_failed", "error", { connected, error: err?.message });
      return;
    }
    setMirrorFolderConnectedState(connected);
    // V2 Step 14d — onboarding action resolve folded from setStorageMode.
    if (connected) {
      supabase.from("actions").update({ status: "done", completed_at: new Date().toISOString() })
        .eq("user_id", user.id).eq("source", "onboarding").eq("feature_tag", "vault").eq("status", "active")
        .then(() => {}).catch((err) => { emitSignal("onboarding_action_resolve_failed", "warning", { error: err?.message }); });
    }
  }, [user]);

  return (
    <VaultContext.Provider
      value={{
        isReady,
        isSupported,
        loading,

        getContext,
        getContextWithMeta,
        getNoteList,
        updateNoteMode,
        recallNotes,

        // Local Vault (gated by mirrorFolderConnected)
        connectVault,
        setConnectedDirectory,
        disconnectVault,
        vaultConnected,
        directoryHandle,
        saveToLocalVault,
        deleteFromLocalVault,

        // Disk import (V2 — make pre-existing folder content searchable)
        scanVault,
        scanInProgress,
        scanProgress,
        lastScanAt,
        lastScanResult,

        // V2 Step 14d — Single source-of-truth. mirror_folder_connected
        // boolean replaces V1 storage_mode toggle entirely. setStorageMode
        // callback removed; setMirrorFolderConnected is the only writer.
        mirrorFolderConnected,
        setMirrorFolderConnected,

        // V2 Step 11a — permission decay state + reconnect callback.
        // Read by V2PermissionBanner via banner-registry condition + render.
        // permissionState values: "granted" (default, no banner) | "decayed"
        // (queryPermission "prompt", show [Reconnect]) | "blocked"
        // (queryPermission "denied", show [Try Again] with cloud-only copy).
        permissionState,
        retryPermission,
      }}
    >
      {children}
    </VaultContext.Provider>
  );
}

export function useVaultContext() {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error("useVaultContext must be used within VaultProvider");
  return ctx;
}
